import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@components/components/ui/avatar';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { UIMessage } from 'ai';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, HTMLAttributes } from 'react';

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role'];
  timestamp?: Date;
};

export const Message = ({
  className,
  from,
  timestamp,
  children,
  ...props
}: MessageProps) => (
  <div
    className={cn(
      'group flex w-full flex-col',
      from === 'user' ? 'is-user items-end' : 'is-assistant items-start',
      className
    )}
    {...props}
  >
    <div
      className={cn(
        'flex items-end gap-2',
        from === 'user' ? 'flex-row' : 'flex-row-reverse'
      )}
    >
      {children}
    </div>
    {timestamp && (
      <div
        className={cn(
          'text-[10px] text-muted-foreground/40 font-light -mt-2',
          from === 'user' ? 'text-right mr-6' : 'text-left ml-6'
        )}
      >
        {formatRelativeTime(timestamp)}
      </div>
    )}
  </div>
);

const messageContentVariants = cva(
  'is-user:dark flex flex-col gap-2 overflow-hidden rounded-lg text-sm',
  {
    variants: {
      variant: {
        contained: [
          'px-4 py-3',
          'group-[.is-user]:text-primary-foreground',
          'group-[.is-assistant]:text-foreground',
        ],
        flat: [
          'group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-foreground',
          'group-[.is-assistant]:text-foreground',
        ],
      },
    },
    defaultVariants: {
      variant: 'contained',
    },
  }
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageContentVariants>;

export const MessageContent = ({
  children,
  className,
  variant,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(messageContentVariants({ variant, className }))}
    {...props}
  >
    {children}
  </div>
);

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src: string;
  name?: string;
};

export const MessageAvatar = ({
  src,
  name,
  className,
  ...props
}: MessageAvatarProps) => (
  <Avatar className={cn('size-8 ring-1 ring-border', className)} {...props}>
    <AvatarImage alt="" className="mt-0 mb-0" src={src} />
    <AvatarFallback>{name?.slice(0, 2) || 'ME'}</AvatarFallback>
  </Avatar>
);
